// src/pages/PatientRecordsPage.jsx
// Route: /patient/records
//
// Patient-facing list of every uploaded document (any record type) — closes
// the gap found while testing this session: after uploading via
// /patient/upload or the chat 📎 attach flow, there was nowhere for the
// patient to see what they'd already uploaded. Only a per-triage-result
// view (/patient/results/:triageId) existed.
//
// Reuses, doesn't reinvent:
//   - healthRecordAPI.list() / getDownloadUrl() — same endpoints
//     HealthRecordsTab.js (navigator/doctor side) already uses successfully,
//     already IDOR-guarded server-side (HealthRecordController's class-level
//     @PreAuthorize).
//   - RECORD_TYPES / getTypeConfig from constants/recordTypes.js — same
//     single source of truth as PatientUploadPage.jsx and HealthRecordsTab.js.
//   - HealthRecordDTO.Response.latestTriage (already returned by the list
//     endpoint, confirmed by reading the DTO — no new backend field needed)
//     to link straight to the full triage report for any blood-test record
//     that has one.
//
// Gap Report (FIGMA_TO_CODE_CHECKLIST §8):
//   ✅ Confirmed from existing code: list/download endpoints, RECORD_TYPES,
//      latestTriage.id already present — no backend changes needed.
//   ⚠️ No Figma frame provided for this screen → follows
//      PatientTriageResultPage.jsx / PatientUploadPage.jsx's existing
//      design language (same card/chip/button patterns) rather than
//      inventing a new visual style.
//   🔗 Reused: PatientAppShell, usePatient(), useToast(), EmptyState,
//      healthRecordAPI, RECORD_TYPES/getTypeConfig
//   🚫 Deviations: none — all colors via var(--token), no Tailwind/MUI

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { useToast } from '../context/ToastContext';
import { healthRecordAPI } from '../services/api';
import { RECORD_TYPES, getTypeConfig } from '../constants/recordTypes';
import EmptyState from '../components/EmptyState';
import PageHead from '../components/PageHead';
import Icon from '../components/Icon';
import HeldReportNotice from '../components/HeldReportNotice';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function chipStyle(active) {
  return {
    padding: '6px 14px', borderRadius: 20,
    border: `1px solid ${active ? 'var(--teal)' : 'var(--border)'}`,
    background: active ? 'var(--teal-bg)' : 'var(--bg-surface)',
    color: active ? 'var(--teal)' : 'var(--text-secondary)',
    fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)',
  };
}

function actionBtnStyle(primary) {
  return {
    padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
    border: `1px solid ${primary ? 'var(--brand-dark)' : 'var(--border)'}`,
    background: primary ? 'var(--brand-dark)' : 'var(--bg-subtle)',
    color: primary ? 'var(--text-on-brand-dark)' : 'var(--text-primary)',
  };
}

export default function PatientRecordsPage() {
  const { patient }  = usePatient();
  const { toast }    = useToast();
  const navigate      = useNavigate();

  const [records, setRecords]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [filterType, setFilterType]     = useState('ALL');
  const [search, setSearch]             = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId]     = useState(null);

  useEffect(() => {
    if (!patient?.id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    healthRecordAPI.list(patient.id)
      .then(res => {
        if (cancelled) return;
        // list() returns a plain array when called without page/size (see
        // HealthRecordController.list()); guard for the paginated shape too
        // in case that ever changes.
        const data = Array.isArray(res.data) ? res.data : (res.data?.content || []);
        const sorted = [...data].sort(
          (a, b) => new Date(b.uploadedAt || 0) - new Date(a.uploadedAt || 0)
        );
        setRecords(sorted);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your documents. Please try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [patient?.id]);

  // Presigned URL generated on demand only, same "never in bulk" principle
  // StorageService documents server-side.
  const handleDownload = useCallback(async (record) => {
    if (!patient?.id) return;
    setDownloadingId(record.id);
    try {
      const res = await healthRecordAPI.getDownloadUrl(patient.id, record.id);
      const win = window.open(res.data.downloadUrl, '_blank', 'noopener');
      if (!win) {
        toast.error('Pop-up blocked — please allow pop-ups for this site and try again.');
      }
    } catch {
      toast.error('Could not generate a download link. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  }, [patient?.id, toast]);

  // Self-service delete — only offered for a record that hasn't been triaged
  // yet (no record.latestTriage — see below), so a patient can pull back a
  // wrong upload and re-upload the correct file, without being able to erase
  // a report staff may already have reviewed. The backend enforces the same
  // cutoff (HealthRecordService.deleteRecord); this just keeps the button
  // from ever being offered for a case the API would reject anyway.
  const handleDelete = useCallback(async (record) => {
    if (!patient?.id) return;
    if (!window.confirm(`Remove "${record.fileName}"? This cannot be undone.`)) return;
    setDeletingId(record.id);
    try {
      await healthRecordAPI.delete(patient.id, record.id);
      setRecords(prev => prev.filter(r => r.id !== record.id));
      toast.success('Document removed.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove this document. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }, [patient?.id, toast]);

  const byType = filterType === 'ALL'
    ? records
    : records.filter(r => r.recordType === filterType);
  const filtered = search.trim()
    ? byType.filter(r => r.fileName?.toLowerCase().includes(search.trim().toLowerCase()))
    : byType;

  const typesPresent = RECORD_TYPES.filter(t => records.some(r => r.recordType === t.value));

  if (loading) {
    return (
      <PatientAppShell breadcrumbs={[{ label: 'My Documents' }]}>
        <div className="page-shell">
          {[1, 2, 3].map(i => (
            <div key={i} style={{
              height: 72, borderRadius: 12, background: 'var(--bg-subtle)',
              marginBottom: 12, animation: 'pulse 1.5s ease-in-out infinite',
            }} />
          ))}
          <style>{`@keyframes pulse { 0%,100%{opacity:.6} 50%{opacity:1} }`}</style>
        </div>
      </PatientAppShell>
    );
  }

  if (error) {
    return (
      <PatientAppShell breadcrumbs={[{ label: 'My Documents' }]}>
        <div className="page-shell">
          <div role="alert" style={{
            padding: 20, borderRadius: 12, background: 'var(--red-bg)',
            color: 'var(--red)', fontSize: 13, fontFamily: 'var(--font-body)',
          }}>
            {error}
          </div>
        </div>
      </PatientAppShell>
    );
  }

  return (
    <PatientAppShell breadcrumbs={[{ label: 'My Documents' }]}>
      <div className="page-shell">
        <PageHead
          eyebrow="Your health journey"
          title="My Documents"
          subtitle="Everything you've uploaded — blood tests, prescriptions, bills, and scans."
          actions={(
            <button
              type="button"
              className="btn btn-accent"
              style={{ whiteSpace: 'nowrap' }}
              onClick={() => navigate('/patient/upload')}
            >
              + Upload
            </button>
          )}
        />

        {records.length === 0 ? (
          <EmptyState
            framed
            icon="folder"
            title="No documents yet"
            message="Upload a lab report, prescription, bill or scan. Previu reads it,
                     extracts your markers and files it here automatically."
            action={{ label: 'Upload a document', onClick: () => navigate('/patient/upload') }}
            secondaryAction={{ label: 'Send it in chat instead', onClick: () => navigate('/patient/chat') }}
            hint="PDF, JPG or PNG — one file or several at once."
          />
        ) : (
          <>
            {/* Search */}
            <div className="search-input-wrap" style={{ maxWidth: 'none', marginBottom: 14 }}>
              <span className="search-icon"><Icon name="search" size={15} /></span>
              <input
                className="search-input"
                placeholder="Search documents…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Filter chips */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
              <button
                type="button"
                onClick={() => setFilterType('ALL')}
                style={chipStyle(filterType === 'ALL')}
              >
                All ({records.length})
              </button>
              {typesPresent.map(t => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setFilterType(t.value)}
                  style={chipStyle(filterType === t.value)}
                >
                  {t.icon} {t.label} ({records.filter(r => r.recordType === t.value).length})
                </button>
              ))}
            </div>

            {filtered.length === 0 && (
              <div style={{
                padding: '20px', textAlign: 'center', fontSize: 13,
                color: 'var(--text-muted)', fontFamily: 'var(--font-body)',
              }}>
                No documents match "{search}".
              </div>
            )}

            {/* Records list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map(record => {
                const typeConfig = getTypeConfig(record.recordType);
                return (
                  <div
                    key={record.id}
                    style={{
                      background: 'var(--bg-surface)', borderRadius: 12, padding: '14px 16px',
                      border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    }}
                  >
                    <div style={{ fontSize: 22, flexShrink: 0 }} aria-hidden="true">
                      {typeConfig.icon}
                    </div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{
                        fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)',
                        fontFamily: 'var(--font-body)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {record.fileName}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-body)' }}>
                        {typeConfig.label} · {formatDate(record.drawDate || record.uploadedAt)}
                        {record.fileSizeBytes ? ` · ${formatSize(record.fileSizeBytes)}` : ''}
                      </div>
                      {/* RecordSource.API is set only by uploadLabPartnerReport (the lab
                          partner sync), so it marks a report that arrived on its own. */}
                      {record.source === 'API' && (
                        <span className="badge badge-confirmed" style={{ marginTop: 4, marginRight: 6, display: 'inline-block' }}>
                          Auto-imported from lab
                        </span>
                      )}
                      <HeldReportNotice record={record} />
                      {/* V108: read-only — retrying is a staff action (HealthRecordsTab.js) */}
                      {record.processingStatus === 'FAILED' && (
                        <span className="badge badge-inactive" style={{ marginTop: 4, display: 'inline-block' }}>
                          Triage failed for this report
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {record.latestTriage && (
                        <button
                          type="button"
                          onClick={() => navigate(`/patient/results/${record.latestTriage.id}`)}
                          style={actionBtnStyle(true)}
                        >
                          📄 View report
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDownload(record)}
                        disabled={downloadingId === record.id}
                        style={actionBtnStyle(false)}
                      >
                        {downloadingId === record.id ? '⏳' : '⬇'} Download
                      </button>
                      {/* Only offered before triage — see handleDelete. Once
                          latestTriage is set the record has a completed
                          report and removing it is a staff action, not
                          self-service. */}
                      {!record.latestTriage && (
                        <button
                          type="button"
                          onClick={() => handleDelete(record)}
                          disabled={deletingId === record.id}
                          title="Remove this document"
                          style={{ ...actionBtnStyle(false), color: 'var(--red)', borderColor: 'var(--red)' }}
                        >
                          {deletingId === record.id ? '⏳' : '🗑'} Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </PatientAppShell>
  );
}
